import {
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  ShakeHandsIcon,
} from "@vericus/cadmus-icons";
import {
  Button,
  colors,
  Divider,
  LinkButton,
  Pill,
  Text,
  vars,
} from "@vericus/cadmus-ui";

import {
  CharterFragment,
  useSetupCharterMutation,
  WorkGroupDocument,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";

import * as styles from "./home-page.css";

interface GroupCharterCardProps {
  /// Student's group
  groupId: string;
  /// Group charter
  charter?: CharterFragment | null;
  /// Group formation is finalised
  finalised?: boolean;
  /// Charter due date
  dueDate?: string | null;
}

export function GroupCharterCard(props: GroupCharterCardProps) {
  const rootNavigate = useRootNavigate();

  const [setupCharter, { loading }] = useSetupCharterMutation();

  const handleSetupCharter = () =>
    setupCharter({
      variables: { groupId: props.groupId },
      refetchQueries: [WorkGroupDocument],
      onCompleted: (data) => {
        if (data?.setupCharter.id) {
          const charterId = data.setupCharter.id;
          rootNavigate(`/charter/${charterId}`);
        }
      },
    });

  // Returns the appropriate CTA button based on charter status
  function charterCTAButton() {
    // Group formation is not finished - charter creation is locked
    if (!props.finalised) {
      return (
        <Button
          kind="secondary"
          icon={<PlusIcon label="" size={24} />}
          onClick={handleSetupCharter}
          disabled={true}
        >
          Group charter
        </Button>
      );
    }

    // Not yet started - charter can be created
    if (!props.charter) {
      return (
        <Button
          kind="secondary"
          icon={<PlusIcon label="" size={24} />}
          onClick={handleSetupCharter}
          disabled={loading}
        >
          Create charter
        </Button>
      );
    }

    // Charter exists and already agreed - can be updated
    if (
      props.charter.latestAgreement &&
      !props.charter.latestAgreement.unlockedAt
    ) {
      return (
        <Button
          kind="outline"
          icon={<EyeIcon label="" size={24} />}
          onClick={() => rootNavigate(`/charter/${props.charter?.id}`)}
          disabled={loading}
        >
          View group charter
        </Button>
      );
    }

    // Default - Continue working on charter
    return (
      <Button
        kind="secondary"
        icon={<PencilIcon label="" size={24} />}
        onClick={() => rootNavigate(`/charter/${props.charter?.id}`)}
        disabled={loading}
      >
        Continue editing
      </Button>
    );
  }

  function charterStatusPill() {
    // Group formation is not finished - charter creation is locked
    if (!props.finalised) {
      return (
        <Pill color={vars.colors.yellow200} textColor={vars.colors.yellow800}>
          Group Formation In Progress
        </Pill>
      );
    }

    // Not yet started
    if (!props.charter) {
      return (
        <Pill color={vars.colors.bgrey400} textColor={vars.colors.navy400}>
          Not Yet Started
        </Pill>
      );
    }

    // Accepted
    if (
      props.charter?.latestAgreement &&
      !props.charter.latestAgreement.unlockedAt
    ) {
      return (
        <Pill color={vars.colors.successBg} textColor={vars.colors.green800}>
          Accepted
        </Pill>
      );
    }

    // Overdue
    if (
      !props.charter?.latestAgreement ||
      props.charter?.latestAgreement.unlockedAt
    ) {
      if (props.dueDate && new Date(props.dueDate) < new Date()) {
        return (
          <Pill color={vars.colors.failureBg} textColor={vars.colors.red800}>
            Overdue - Acceptance Pending
          </Pill>
        );
      }

      // In progress and not accepted yet
      if (props.dueDate) {
        return (
          <Pill color={vars.colors.yellow200} textColor={vars.colors.yellow800}>
            In Progress - Acceptance Pending
          </Pill>
        );
      }
    }
  }

  return (
    <div className={styles.steps}>
      <div className={styles.stepIcon["initial"]}>
        <ShakeHandsIcon label="" size={30} />
      </div>
      <div className={styles.stepCard}>
        <div className={styles.charterCardContainer}>
          <div>
            <Text kind="headingFour">Group Charter</Text>
            <Text kind="bodyLine">
              Your assessment requires a Group Charter. You can create one after
              your group is finalised. To know more about Group charters and
              tips for creating an effective one, you can find resources at the
              below link
            </Text>
            <br />
            <div className={styles.charterStatus}>{charterStatusPill()}</div>
            {!props.charter?.latestAgreement && (
              <>
                <Divider />
                <div className={styles.charterCardBottom}>
                  {props.dueDate && (
                    <Text kind="bodySm">
                      <strong>Due:</strong>{" "}
                      {formatDateFull(new Date(props.dueDate))}
                    </Text>
                  )}

                  <a
                    href="https://support.cadmus.io/tips-for-creating-group-charter"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkButton
                      kind="primary"
                      rightIcon={
                        <ArrowTopRightOnSquareIcon
                          size={20}
                          label=""
                          color={colors.primary}
                        />
                      }
                    >
                      Tips for creating Group Charter
                    </LinkButton>
                  </a>
                </div>
              </>
            )}
          </div>
          <div>{charterCTAButton()}</div>
        </div>
      </div>
    </div>
  );
}
